import { useToast } from "@/components/ui/use-toast";
import {
  dismissCandidate,
  evaluateAllCandidates,
  evaluateCandidate,
  promoteCandidate,
  resetBaseline,
  rollbackUseCase,
} from "@/services/adminModelCurrency";
import AttentionSummary from "./AttentionSummary";
import CheckNowButton from "./CheckNowButton";
import HiddenCandidatesNote from "./HiddenCandidatesNote";
import LastCheckLine from "./LastCheckLine";
import LoadErrorNotice from "./LoadErrorNotice";
import PriceFootnote from "./PriceFootnote";
import TestAllButton from "./TestAllButton";
import UseCaseCard from "./UseCaseCard";
import { READ_ONLY_NOTE, countRunnableWaiting, describeApiError, hasEvaluating } from "./helpers";
import { useEvalCompletionToasts } from "./useEvalCompletionToasts";
import { useModelCurrencyOverview } from "./useModelCurrencyOverview";
import { useOverviewPolling } from "./useOverviewPolling";

const TEST_STARTED = "Test started. This takes a few minutes; this page updates by itself.";

function startedMessage(result: { started: number }): string {
  if (result.started < 1) return "No tests to start.";
  const tests = result.started === 1 ? "1 test" : `${result.started} tests`;
  return `Started ${tests}. This takes a few minutes; this page updates by itself.`;
}

const ModelsTab = () => {
  const { toast } = useToast();
  const { overview, loading, error, refresh, retry } = useModelCurrencyOverview();
  useOverviewPolling(overview, refresh);
  useEvalCompletionToasts(overview);

  /** Run an API action; toast success, or the readable error and rethrow so the dialog stays open. */
  async function act(run: () => Promise<unknown>, success: string | ((result: never) => string)) {
    try {
      const result = await run();
      toast({ title: typeof success === "string" ? success : success(result as never) });
      await refresh();
    } catch (err) {
      toast({
        title: "That didn't work",
        description: describeApiError(err),
        variant: "destructive",
      });
      throw err;
    }
  }

  if (loading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (!overview) {
    return <LoadErrorNotice message={error} onRetry={retry} />;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-4 rounded-lg border bg-muted/30 p-4">
        <LastCheckLine state={overview.checkState} />
        <div className="flex items-center gap-2">
          <TestAllButton
            writable={overview.writable}
            count={countRunnableWaiting(overview)}
            busy={hasEvaluating(overview)}
            onRunAll={() => act(() => evaluateAllCandidates(), startedMessage)}
          />
          <CheckNowButton onDone={refresh} />
        </div>
      </div>
      <AttentionSummary overview={overview} />
      {!overview.writable ? (
        <p className="text-sm text-muted-foreground">{READ_ONLY_NOTE}</p>
      ) : null}
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          Couldn't refresh: {error}
        </p>
      ) : null}
      <div className="divide-y rounded-lg border bg-card">
        {overview.useCases.map(useCase => (
          <UseCaseCard
            key={useCase.id}
            useCase={useCase}
            writable={overview.writable}
            onPromote={id => act(() => promoteCandidate(id), "Model promoted")}
            onDismiss={id => act(() => dismissCandidate(id), "Candidate dismissed")}
            onRunTest={id => act(() => evaluateCandidate(id), TEST_STARTED)}
            onRollback={id => act(() => rollbackUseCase(id), "Rolled back")}
            onResetBaseline={id => act(() => resetBaseline(id), "Baseline marked as reset")}
          />
        ))}
      </div>
      <div className="space-y-1">
        <PriceFootnote priceTable={overview.priceTable} />
        <HiddenCandidatesNote count={overview.hiddenCandidates} />
      </div>
    </div>
  );
};

export default ModelsTab;
